import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

export type SignupContinuityCopy = {
  almostThere: string;
  contextTitle: string;
  contextLead: string;
  contextNoLoss: string;
  signalLabels: {
    blocker: string;
    confusion: string;
    outcome: string;
  };
  signalFallbacks: {
    blocker: string;
    confusion: string;
    outcome: string;
  };
  pathTitle: string;
  pathSteps: string[];
  formTitle: string;
  formSubtitle: string;
  optionalQuestion: string;
  optionalPlaceholder: string;
  cta: string;
  ctaLoading: string;
  reassurance: string;
  successTitle: string;
  successDescription: string;
  successAction: string;
};

export const signupContinuityCopyByLocale = defineLocaleRecord<SignupContinuityCopy>({
  en: {
    almostThere: "Almost there",
    contextTitle: "Based on what you told us",
    contextLead: "We kept your onboarding answers so your next step stays clear.",
    contextNoLoss: "No more decisions are required. Nothing you shared was wasted.",
    signalLabels: {
      blocker: "Selected blocker",
      confusion: "Where it felt confusing",
      outcome: "What you want to make",
    },
    signalFallbacks: {
      blocker: "You want a clear starting point",
      confusion: "You want fewer choices",
      outcome: "You want a practical result",
    },
    pathTitle: "What happens after you sign up?",
    pathSteps: [
      "We start with one blocker.",
      "You get one clear next step.",
      "You can finish something today.",
    ],
    formTitle: "Save your path",
    formSubtitle: "This saves your progress so you can continue where you left off.",
    optionalQuestion: "What do you want to build first? (optional)",
    optionalPlaceholder: "A simple website for my idea...",
    cta: "Save my progress",
    ctaLoading: "Saving your progress...",
    reassurance: "No spam. No pressure. You can leave anytime.",
    successTitle: "Your path is saved",
    successDescription: "Check your email to confirm your account and continue with this plan.",
    successAction: "Continue with this plan",
  },
  fr: {
    almostThere: "Presque fini",
    contextTitle: "Selon ce que vous nous avez dit",
    contextLead: "Nous avons garde vos reponses pour garder la suite claire.",
    contextNoLoss: "Aucune nouvelle decision. Rien de ce que vous avez partage n'est perdu.",
    signalLabels: {
      blocker: "Blocage choisi",
      confusion: "Point de confusion",
      outcome: "Ce que vous voulez creer",
    },
    signalFallbacks: {
      blocker: "Vous voulez un point de depart clair",
      confusion: "Vous voulez moins de choix",
      outcome: "Vous voulez un resultat concret",
    },
    pathTitle: "Que se passe-t-il apres l'inscription ?",
    pathSteps: [
      "Nous commencons par un seul blocage.",
      "Vous recevez une seule prochaine etape claire.",
      "Vous pouvez terminer quelque chose aujourd'hui.",
    ],
    formTitle: "Sauvegardez votre chemin",
    formSubtitle: "Cela sauvegarde votre progression pour reprendre au meme endroit.",
    optionalQuestion: "Que voulez-vous creer en premier ? (optionnel)",
    optionalPlaceholder: "Un site simple pour mon idee...",
    cta: "Sauvegarder ma progression",
    ctaLoading: "Sauvegarde en cours...",
    reassurance: "Pas de spam. Pas de pression. Vous pouvez partir quand vous voulez.",
    successTitle: "Votre chemin est sauvegarde",
    successDescription: "Verifiez votre email pour confirmer votre compte et continuer ce plan.",
    successAction: "Continuer avec ce plan",
  },
  ru: {
    almostThere: "Почти готово",
    contextTitle: "На основе того, что вы нам сказали",
    contextLead: "Мы сохранили ваши ответы из онбординга, чтобы следующий шаг был понятным.",
    contextNoLoss: "Больше никаких решений не нужно. Ничего из того, что вы указали, не потеряно.",
    signalLabels: {
      blocker: "Выбранный блокер",
      confusion: "Где было непонятно",
      outcome: "Что вы хотите сделать",
    },
    signalFallbacks: {
      blocker: "Вам нужна понятная точка старта",
      confusion: "Вам нужно меньше вариантов",
      outcome: "Вам нужен практичный результат",
    },
    pathTitle: "Что будет после регистрации?",
    pathSteps: [
      "Начнем с одного блокера.",
      "Вы получите один ясный следующий шаг.",
      "Сегодня можно довести что-то до результата.",
    ],
    formTitle: "Сохраните ваш путь",
    formSubtitle: "Это сохранит прогресс, чтобы вы продолжили с того же места.",
    optionalQuestion: "Что вы хотите сделать в первую очередь? (необязательно)",
    optionalPlaceholder: "Простой сайт для моей идеи...",
    cta: "Сохранить мой прогресс",
    ctaLoading: "Сохраняем прогресс...",
    reassurance: "Без спама. Без давления. Вы можете уйти в любой момент.",
    successTitle: "Ваш путь сохранен",
    successDescription: "Проверьте почту, подтвердите аккаунт и продолжите по этому плану.",
    successAction: "Продолжить по этому плану",
  },
  uk: {
    almostThere: "Майже готово",
    contextTitle: "На основі того, що ви нам сказали",
    contextLead: "Ми зберегли ваші відповіді з онбордингу, щоб наступний крок був зрозумілим.",
    contextNoLoss: "Більше жодних рішень не потрібно. Нічого з того, що ви вказали, не втрачено.",
    signalLabels: {
      blocker: "Обраний блокер",
      confusion: "Де було незрозуміло",
      outcome: "Що ви хочете зробити",
    },
    signalFallbacks: {
      blocker: "Вам потрібна чітка точка старту",
      confusion: "Вам потрібно менше варіантів",
      outcome: "Вам потрібен практичний результат",
    },
    pathTitle: "Що буде після реєстрації?",
    pathSteps: [
      "Почнемо з одного блокера.",
      "Ви отримаєте один ясний наступний крок.",
      "Сьогодні можна завершити щось до результату.",
    ],
    formTitle: "Збережіть ваш шлях",
    formSubtitle: "Це збереже прогрес, щоб ви продовжили з того ж місця.",
    optionalQuestion: "Що ви хочете зробити в першу чергу? (необов'язково)",
    optionalPlaceholder: "Простий сайт для моєї ідеї...",
    cta: "Зберегти мій прогрес",
    ctaLoading: "Зберігаємо прогрес...",
    reassurance: "Без спаму. Без тиску. Ви можете піти в будь-який момент.",
    successTitle: "Ваш шлях збережено",
    successDescription: "Перевірте пошту, підтвердьте акаунт і продовжте за цим планом.",
    successAction: "Продовжити за цим планом",
  },
  de: {
    almostThere: "Fast geschafft",
    contextTitle: "Basierend auf dem, was du uns gesagt hast",
    contextLead: "Wir haben deine Antworten aus dem Onboarding gespeichert, damit der naechste Schritt klar bleibt.",
    contextNoLoss: "Keine weiteren Entscheidungen noetig. Nichts von deinen Angaben geht verloren.",
    signalLabels: {
      blocker: "Gewaehlter Blocker",
      confusion: "Wo es unklar war",
      outcome: "Was du bauen willst",
    },
    signalFallbacks: {
      blocker: "Du willst einen klaren Startpunkt",
      confusion: "Du willst weniger Auswahl",
      outcome: "Du willst ein praktisches Ergebnis",
    },
    pathTitle: "Was passiert nach der Anmeldung?",
    pathSteps: [
      "Wir starten mit genau einem Blocker.",
      "Du bekommst genau einen klaren naechsten Schritt.",
      "Du kannst heute etwas fertigstellen.",
    ],
    formTitle: "Speichere deinen Pfad",
    formSubtitle: "Damit speichern wir deinen Fortschritt, damit du genau hier weitermachen kannst.",
    optionalQuestion: "Was willst du zuerst bauen? (optional)",
    optionalPlaceholder: "Eine einfache Website fuer meine Idee...",
    cta: "Meinen Fortschritt speichern",
    ctaLoading: "Fortschritt wird gespeichert...",
    reassurance: "Kein Spam. Kein Druck. Du kannst jederzeit gehen.",
    successTitle: "Dein Pfad ist gespeichert",
    successDescription: "Pruefe deine E-Mail, bestaetige dein Konto und mach mit diesem Plan weiter.",
    successAction: "Mit diesem Plan fortfahren",
  },
  es: {
    almostThere: "Casi listo",
    contextTitle: "Segun lo que nos contaste",
    contextLead: "Guardamos tus respuestas del onboarding para mantener claro tu siguiente paso.",
    contextNoLoss: "No necesitas tomar mas decisiones. Nada de lo que compartiste se pierde.",
    signalLabels: {
      blocker: "Bloqueo elegido",
      confusion: "Donde se sintio confusion",
      outcome: "Lo que quieres crear",
    },
    signalFallbacks: {
      blocker: "Quieres un punto de inicio claro",
      confusion: "Quieres menos opciones",
      outcome: "Quieres un resultado practico",
    },
    pathTitle: "¿Que pasa despues de registrarte?",
    pathSteps: [
      "Empezamos con un solo bloqueo.",
      "Recibes un solo siguiente paso claro.",
      "Puedes terminar algo hoy.",
    ],
    formTitle: "Guarda tu camino",
    formSubtitle: "Esto guarda tu progreso para continuar exactamente donde lo dejaste.",
    optionalQuestion: "¿Que quieres crear primero? (opcional)",
    optionalPlaceholder: "Un sitio simple para mi idea...",
    cta: "Guardar mi progreso",
    ctaLoading: "Guardando tu progreso...",
    reassurance: "Sin spam. Sin presion. Puedes salir cuando quieras.",
    successTitle: "Tu camino esta guardado",
    successDescription: "Revisa tu email, confirma tu cuenta y continua con este plan.",
    successAction: "Continuar con este plan",
  },
  it: {
    almostThere: "Ci siamo quasi",
    contextTitle: "In base a quello che ci hai detto",
    contextLead: "Abbiamo salvato le tue risposte dell'onboarding per tenere chiaro il prossimo passo.",
    contextNoLoss: "Non servono altre decisioni. Nulla di cio che hai condiviso va perso.",
    signalLabels: {
      blocker: "Blocco scelto",
      confusion: "Dove era poco chiaro",
      outcome: "Cosa vuoi creare",
    },
    signalFallbacks: {
      blocker: "Vuoi un punto di partenza chiaro",
      confusion: "Vuoi meno scelte",
      outcome: "Vuoi un risultato pratico",
    },
    pathTitle: "Cosa succede dopo la registrazione?",
    pathSteps: [
      "Partiamo da un solo blocco.",
      "Ricevi un solo prossimo passo chiaro.",
      "Puoi completare qualcosa oggi.",
    ],
    formTitle: "Salva il tuo percorso",
    formSubtitle: "Questo salva i tuoi progressi per continuare da dove hai lasciato.",
    optionalQuestion: "Cosa vuoi creare per prima cosa? (opzionale)",
    optionalPlaceholder: "Un sito semplice per la mia idea...",
    cta: "Salva i miei progressi",
    ctaLoading: "Salvataggio in corso...",
    reassurance: "Niente spam. Nessuna pressione. Puoi uscire quando vuoi.",
    successTitle: "Il tuo percorso e salvato",
    successDescription: "Controlla l'email, conferma l'account e continua con questo piano.",
    successAction: "Continua con questo piano",
  },
  pt: {
    almostThere: "Quase la",
    contextTitle: "Com base no que voce nos contou",
    contextLead: "Mantivemos suas respostas do onboarding para que seu proximo passo fique claro.",
    contextNoLoss: "Nenhuma nova decisao e necessaria. Nada do que voce compartilhou foi perdido.",
    signalLabels: {
      blocker: "Bloqueio selecionado",
      confusion: "Onde pareceu confuso",
      outcome: "O que voce quer criar",
    },
    signalFallbacks: {
      blocker: "Voce quer um ponto de partida claro",
      confusion: "Voce quer menos opcoes",
      outcome: "Voce quer um resultado pratico",
    },
    pathTitle: "O que acontece apos o cadastro?",
    pathSteps: [
      "Comecamos com um unico bloqueio.",
      "Voce recebe um unico proximo passo claro.",
      "Voce pode concluir algo hoje.",
    ],
    formTitle: "Salve seu caminho",
    formSubtitle: "Isso salva seu progresso para que voce continue de onde parou.",
    optionalQuestion: "O que voce quer criar primeiro? (opcional)",
    optionalPlaceholder: "Um site simples para minha ideia...",
    cta: "Salvar meu progresso",
    ctaLoading: "Salvando seu progresso...",
    reassurance: "Sem spam. Sem pressao. Voce pode sair quando quiser.",
    successTitle: "Seu caminho esta salvo",
    successDescription: "Verifique seu email para confirmar sua conta e continuar com este plano.",
    successAction: "Continuar com este plano",
  },
});

export function getSignupContinuityCopy(lang: Locale): SignupContinuityCopy {
  return signupContinuityCopyByLocale[lang];
}
